import React, { useState, useEffect } from "react";
import api from "@/lib/api";
import { money, num } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Truck, Route, Loader2, MapPin } from "lucide-react";
import StateCountyPicker from "@/components/StateCountyPicker";

const KNOBS = [
  ["crew_per_truck", "Crew per truck"],
  ["extra_trucks", "Extra trucks"],
  ["round_trips", "Round trips"],
  ["truck_mpg", "Truck mpg"],
  ["truck_rate_per_mile", "Truck $/mi (0 = live fuel)"],
  ["lowboy_rate", "Lowboy $/mi"],
  ["equipment_loads", "Equipment loads (lowboy)"],
  ["travel_days", "Travel days"],
];
const DEFAULTS = { crew_per_truck: 4, extra_trucks: 0, round_trips: 1, truck_mpg: 12, truck_rate_per_mile: 0, lowboy_rate: 5.5, equipment_loads: 0, travel_days: 2, include_crew_labor: true };

export default function MobilizationTab({ bid, computed, updateBid, readOnly }) {
  const cfg = bid.mobilization || {};
  const mob = computed?.mobilization;
  const blend = computed?.crew_blend || {};
  const mgrTrucks = computed?.management?.trucks || 0;
  const profile = bid.location_profile || {};
  const [st, setSt] = useState(cfg.hq_state || "");
  const [county, setCounty] = useState(cfg.hq_county || "");
  const [zip, setZip] = useState(cfg.hq_zip || "");
  const [loading, setLoading] = useState(false);
  const [fromOffice, setFromOffice] = useState(false);

  // Seed the crew HQ from the company office/HQ (Company Settings) when nothing is set yet.
  useEffect(() => {
    if (cfg.hq_state || cfg.hq_county) return;
    api.get("/branding").then((r) => {
      const o = r.data || {};
      if (o.office_state || o.office_county) {
        setSt((s) => s || (o.office_state || ""));
        setCounty((c) => c || (o.office_county || ""));
        setZip((z) => z || (o.office_zip || ""));
        setFromOffice(true);
      }
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const patch = (next) => updateBid({ mobilization: { ...cfg, ...next } });
  const enable = () => patch({ enabled: true, include_in_bid: true, ...DEFAULTS });
  const setField = (k, val) => patch({ [k]: val === "" ? "" : Number(val) });

  const resolve = async () => {
    if (!st) { toast.error("Pick a state for the crew office / HQ."); return; }
    const hqStr = (zip || "").trim() || (county ? `${county}, ${st}` : st);
    setLoading(true);
    try {
      const geo = profile.geo || {};
      const r = await api.post("/location/route-distance", {
        hq: hqStr, hq_state: st, hq_county: county,
        site: { lat: geo.lat, lon: geo.lon, resolved: geo.resolved, state: bid.state, county: bid.location_county },
      });
      if (r.data.status !== "ok") { toast.error(r.data.warnings?.[0] || "Could not resolve the route"); }
      else {
        patch({ hq: hqStr, hq_state: st, hq_county: county, hq_zip: (zip || "").trim(), hq_resolved: r.data.hq_resolved, site_resolved: r.data.site_resolved, distance_mi: r.data.distance_mi, drive_hours: r.data.drive_hours });
        toast.success(`${r.data.distance_mi} mi · ${r.data.drive_hours} h each way`);
      }
    } catch (e) { toast.error("Could not resolve the route"); }
    finally { setLoading(false); }
  };

  if (!cfg.enabled) {
    return (
      <div className="p-6 max-w-[1800px] w-full mx-auto">
        <Card className="p-12 text-center" data-testid="mob-disabled">
          <Truck className="w-8 h-8 text-slate-300 mx-auto mb-3" />
          <h2 className="text-sm font-semibold text-slate-700">Mobilization</h2>
          <p className="text-xs text-slate-500 mt-1 mb-4 max-w-md mx-auto">Move crews, managers and equipment to site. Trucks auto-tally from your crew headcount and management, priced live off the real drive distance, diesel and the blended crew rate + per-diem. It carries <b>$0</b> until enabled; nothing is pre-filled.</p>
          <Button onClick={enable} disabled={readOnly} className="bg-[#F4C400] text-black hover:brightness-95" data-testid="mob-enable">Enable mobilization</Button>
        </Card>
      </div>
    );
  }

  const included = cfg.include_in_bid !== false;
  const trucks = mob?.trucks || {};
  const dist = cfg.distance_mi || mob?.distance_mi || 0;

  return (
    <div className="p-5 max-w-[1800px] w-full mx-auto" data-testid="mob-panel">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div className="flex items-center gap-2 text-sm font-semibold text-slate-700"><Truck className="w-4 h-4 text-[#003E87]" /> Mobilization</div>
        <div className="flex items-center gap-3 flex-wrap text-xs">
          {dist ? <span className="text-slate-500 flex items-center gap-1"><Route className="w-3.5 h-3.5" /> {dist} mi · {cfg.drive_hours || mob?.drive_hours} h each way</span> : null}
          <div className="flex items-center gap-1.5"><span className="text-[10px] uppercase text-slate-400">Include in bid</span>
            <Switch checked={included} onCheckedChange={(v) => patch({ include_in_bid: v })} disabled={readOnly} data-testid="mob-include" /></div>
          <Button size="sm" variant="ghost" onClick={() => patch({ enabled: false })} disabled={readOnly} className="text-slate-400 hover:text-rose-600 text-xs" data-testid="mob-disable">Turn off</Button>
        </div>
      </div>

      {/* HQ picker + distance */}
      <Card className="p-4 mb-4">
        <div className="flex items-center justify-between mb-2">
          <label className="text-[10px] uppercase text-slate-400 flex items-center gap-1"><MapPin className="w-3 h-3" /> Crew office / HQ</label>
          {fromOffice && !cfg.hq_state && <span className="text-[10px] text-[#003E87]" data-testid="mob-hq-from-office">defaulted from company office</span>}
        </div>
        <div className="grid sm:grid-cols-[minmax(0,1fr)_120px_auto] gap-3 items-end">
          <StateCountyPicker idPrefix="mob-hq" state={st} county={county}
            onChange={({ state, county: c }) => { setSt(state); setCounty(c); setFromOffice(false); }} disabled={readOnly} />
          <div>
            <label className="text-[10px] uppercase text-slate-400">Zip (optional)</label>
            <Input value={zip} onChange={(e) => { setZip(e.target.value); setFromOffice(false); }} placeholder="79701" className="h-9 font-mono" data-testid="mob-hq-zip" disabled={readOnly} />
          </div>
          <Button onClick={resolve} disabled={loading || readOnly} className="bg-[#003E87] hover:bg-[#00305F] h-9" data-testid="mob-resolve">
            {loading ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Route className="w-4 h-4 mr-1.5" />} Resolve distance
          </Button>
        </div>
        {cfg.hq_resolved && <div className="text-[11px] text-slate-400 mt-2" data-testid="mob-distance">{cfg.hq_resolved} → {cfg.site_resolved || "site"} · <b className="text-slate-700 font-mono">{dist} mi</b></div>}
        {!dist && <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-100 rounded px-2.5 py-1.5 mt-3" data-testid="mob-no-distance">No drive distance yet — resolve the HQ to price truck fuel and equipment freight. Crew travel labor and per-diem still compute from travel days.</div>}
      </Card>

      {/* Auto-tally trucks */}
      <div className="grid sm:grid-cols-4 gap-3 mb-4">
        <Card className="p-3" data-testid="mob-total-card"><div className="text-[10px] uppercase text-slate-400">Mobilization total</div><div className="text-xl font-bold font-mono text-black" data-testid="mob-total">{money(mob?.total || 0)}</div><div className="text-[10px] text-slate-400">{included ? "included in the bid" : "excluded"}</div></Card>
        <Card className="p-3"><div className="text-[10px] uppercase text-slate-400 flex items-center gap-1"><Truck className="w-3 h-3" /> Crew trucks</div><div className="text-sm font-mono text-slate-800" data-testid="mob-trucks-crew">{trucks.crew ?? 0}</div><div className="text-[10px] text-slate-400">{blend.headcount || 0} crew ÷ {cfg.crew_per_truck || 0}/truck</div></Card>
        <Card className="p-3"><div className="text-[10px] uppercase text-slate-400">Manager trucks</div><div className="text-sm font-mono text-slate-800" data-testid="mob-trucks-manager">{trucks.manager ?? mgrTrucks}</div><div className="text-[10px] text-slate-400">auto from Management</div></Card>
        <Card className="p-3"><div className="text-[10px] uppercase text-slate-400">Total trucks</div><div className="text-sm font-mono text-slate-800" data-testid="mob-trucks-total">{trucks.total ?? 0}</div><div className="text-[10px] text-slate-400">crew + mgr + extra</div></Card>
      </div>

      {!included && <div className="mb-4 rounded-md border border-slate-300 bg-slate-50 px-4 py-2 text-xs text-slate-600" data-testid="mob-excluded-note">Mobilization is <b>configured but excluded</b> from this bid's total. Flip “Include in bid” to roll it in.</div>}

      {/* Knobs */}
      <Card className="p-4 mb-4">
        <div className="text-[10px] uppercase tracking-wide text-slate-400 mb-2">Assumptions (all editable)</div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {KNOBS.map(([k, label]) => (
            <div key={k}>
              <label className="text-[10px] uppercase text-slate-400">{label}</label>
              <Input type="number" step="any" value={cfg[k] ?? ""} onChange={(e) => setField(k, e.target.value)} className="h-8 text-xs font-mono" data-testid={`mob-field-${k}`} disabled={readOnly} />
            </div>
          ))}
          <label className="flex items-center gap-2 col-span-2 sm:col-span-1 mt-4">
            <Switch checked={cfg.include_crew_labor !== false} onCheckedChange={(v) => patch({ include_crew_labor: v })} disabled={readOnly} data-testid="mob-include-crew-labor" />
            <span className="text-[11px] text-slate-600">Pay crew for travel days</span>
          </label>
        </div>
      </Card>

      {/* Auditable line items */}
      {mob && mob.lines && mob.lines.length > 0 ? (
        <Card className="overflow-hidden" data-testid="mob-result">
          {mob.lines.map((it, i) => (
            <div key={i} className="flex items-center justify-between px-4 py-2 border-b border-slate-50 text-xs" data-testid={`mob-line-${it.kind}`}>
              <div className="min-w-0"><div className="font-medium text-slate-700 truncate">{it.label}</div><div className="text-[10px] text-slate-400 font-mono truncate">{it.detail}</div></div>
              <div className="font-mono font-semibold text-slate-800 shrink-0">{money(it.amount)}</div>
            </div>
          ))}
          <div className="flex items-center justify-between px-4 py-2 bg-slate-50">
            <span className="text-xs font-semibold text-slate-600">Mobilization total</span>
            <span className="text-sm font-mono font-bold text-black">{money(mob.total)}</span>
          </div>
        </Card>
      ) : (
        <Card className="p-8 text-center" data-testid="mob-empty"><div className="text-xs text-slate-500">Nothing to mobilize yet.</div><div className="text-[11px] text-slate-400 mt-0.5">Resolve a drive distance and set loads / travel days — trucks auto-tally from your crew ({blend.headcount || 0}) and management ({mgrTrucks}).</div></Card>
      )}

      <p className="text-[10px] text-slate-400 mt-3">Trucks auto-tally: crew ({num(blend.headcount || 0)} ÷ {cfg.crew_per_truck || 0}) + {mgrTrucks} manager + extras. Truck travel prices off {cfg.truck_rate_per_mile ? `$${cfg.truck_rate_per_mile}/mi` : "live diesel ÷ mpg"}; equipment freight is loads × miles × $/mi; crew travel uses the blended crew rate ({money(blend.effective_per_manhour || 0)}/hr) and blended per-diem ({money(blend.blended_per_diem || 0)}/day). Rolls into the bid only while “Include in bid” is on.</p>
    </div>
  );
}
